import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { listAdherentsPayments, refundMembershipPayment, requestMembershipPayment } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Euro, RefreshCw, Undo2, Plus, ExternalLink, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const META = {
  paid:     { label: "Payé",       color: "bg-emerald-100 text-emerald-800" },
  unpaid:   { label: "À payer",    color: "bg-orange-100 text-orange-800" },
  refunded: { label: "Remboursé",  color: "bg-blue-100 text-blue-800" },
  lifetime: { label: "À vie",      color: "bg-amber-100 text-amber-800" },
  failed:   { label: "Échec",      color: "bg-red-100 text-red-800" },
  legacy:   { label: "Manuel",     color: "bg-gray-100 text-gray-700" },
};

const FILTERS = ["all", "paid", "unpaid", "refunded", "lifetime", "failed", "legacy"];

const fmt = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "-";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR") : "-";

export default function PaymentsListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [newDialog, setNewDialog] = useState({ open: false });
  const [candidates, setCandidates] = useState([]);
  const [pricing, setPricing] = useState({ standard_annual_amount: 39.0, currency: "EUR" });
  const [form, setForm] = useState({ membership_id: "", motif: "Adhésion annuelle", amount: "", period_start: "", period_end: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { payment_status: filter };
      const { data } = await listAdherentsPayments(params);
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement paiements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openNewDialog = async () => {
    try {
      const [{ data: pr }, { data: mems }] = await Promise.all([
        axios.get(`${API}/payments/pricing`),
        axios.get(`${API}/memberships?limit=500`),
      ]);
      setPricing(pr);
      const eligible = (mems || []).filter(m =>
        m.status === "active" &&
        m.validity_type === "annual" &&
        !["founder", "partner"].includes(m.membership_type) &&
        m.pricing_type !== "complimentary"
      );
      setCandidates(eligible);
      setForm({
        membership_id: "",
        motif: "Adhésion annuelle",
        amount: pr.standard_annual_amount.toFixed(2),
        period_start: "",
        period_end: "",
      });
      setNewDialog({ open: true });
    } catch (e) {
      toast.error("Chargement adhérents impossible");
    }
  };

  const submitNewPayment = async () => {
    if (!form.membership_id) return toast.error("Sélectionnez un adhérent");
    const amt = parseFloat(form.amount);
    if (!(amt > 0)) return toast.error("Montant invalide");
    setBusy(true);
    try {
      const payload = {
        motif: form.motif || "Adhésion annuelle",
        amount: amt,
        period_start: form.period_start || null,
        period_end: form.period_end || null,
      };
      const { data } = await requestMembershipPayment(form.membership_id, payload);
      if (data.checkout_url) {
        toast.success(`Lien de paiement créé (${data.amount} ${data.currency})`);
        window.open(data.checkout_url, "_blank");
      }
      setNewDialog({ open: false });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création paiement");
    } finally { setBusy(false); }
  };

  const handleRefund = async (r) => {
    if (!r.external_transaction_id) return;
    const msg = [
      `Rembourser le paiement SumUp Sandbox pour ${r.member_number} ?`,
      `Montant : ${r.payment_amount?.toFixed(2)} ${r.payment_currency || "EUR"}`,
      `Transaction : ${r.external_transaction_id}`,
      "",
      "⚠️ Ce remboursement ne suspend pas automatiquement l'adhésion.",
    ].join("\n");
    if (!window.confirm(msg)) return;
    try {
      const { data } = await refundMembershipPayment(r.membership_id, "admin_action");
      if (data.already_refunded) toast.info("Ce paiement était déjà remboursé");
      else toast.success(`Remboursement demandé (${data.refund_status || "requested"})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur remboursement");
    }
  };

  return (
    <div data-testid="payments-list-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
          <Euro className="h-7 w-7" /> Paiements / cotisations
        </h1>
        <div className="flex items-center gap-2">
          <Button
            onClick={openNewDialog}
            className="bg-accent hover:bg-accent/90 gap-2"
            data-testid="new-payment-btn"
          >
            <Plus className="h-4 w-4" /> Nouveau paiement
          </Button>
          <button onClick={load} className="p-2 rounded hover:bg-muted" title="Actualiser">
            <RefreshCw className="h-4 w-4" />
          </button>
          <div className="flex gap-1">
            {FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 h-9 text-xs rounded-md border transition ${filter === f ? "bg-primary text-white border-primary" : "bg-white text-muted-foreground border-border hover:border-primary/50"}`}
                data-testid={`payments-filter-${f}`}
              >
                {f === "all" ? "Tous" : META[f]?.label || f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <Euro className="h-10 w-10 mx-auto mb-2 opacity-30" />Aucun paiement pour ce filtre.
        </CardContent></Card>
      ) : (
        <div className="bg-white border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Membre</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Période</th>
                  <th className="px-4 py-3 text-left">Montant</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                  <th className="px-4 py-3 text-left">Date paiement</th>
                  <th className="px-4 py-3 text-left">Transaction SumUp</th>
                  <th className="px-4 py-3 text-left">Remboursement</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map(r => {
                  const meta = META[r.payment_status] || META.legacy;
                  const canRefund = r.external_transaction_id && !r.refund_id;
                  return (
                    <tr key={r.membership_id} className="hover:bg-muted/10" data-testid={`payment-row-${r.membership_id}`}>
                      <td className="px-4 py-3">
                        <Link to={`/dashboard/memberships`} className="font-medium text-primary hover:underline">
                          {r.first_name} {r.last_name}
                        </Link>
                        <div className="text-xs font-mono text-muted-foreground">{r.public_member_id || r.member_number}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {r.membership_type} · {r.validity_type}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {r.validity_type === "lifetime" ? "Permanente" : `${fmtDate(r.valid_from)} → ${fmtDate(r.valid_until)}`}
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {r.payment_amount != null ? `${r.payment_amount.toFixed(2)} ${r.payment_currency || "EUR"}` : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${meta.color}`}>{meta.label}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{fmt(r.paid_at)}</td>
                      <td className="px-4 py-3 font-mono text-[11px]" title={r.external_transaction_id || ""}>
                        {r.external_transaction_id ? `${r.external_transaction_id.slice(0, 8)}…` : "-"}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {r.refund_id ? (
                          <div>
                            <div className="font-mono">{r.refund_id.slice(0, 12)}…</div>
                            <div className="text-muted-foreground">{fmt(r.refunded_at)}</div>
                          </div>
                        ) : "-"}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canRefund ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRefund(r)}
                            data-testid={`payments-refund-${r.membership_id}`}
                            className="gap-1.5"
                          >
                            <Undo2 className="h-3.5 w-3.5" /> Rembourser
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={newDialog.open} onOpenChange={(v) => !v && setNewDialog({ open: false })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary flex items-center gap-2">
              <Euro className="h-5 w-5" /> Nouveau paiement
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label>Adhérent *</Label>
              <select
                className="w-full h-10 border border-input bg-background px-3 rounded-md text-sm"
                value={form.membership_id}
                onChange={(e) => setForm(f => ({ ...f, membership_id: e.target.value }))}
                data-testid="new-payment-adherent"
              >
                <option value="">— Sélectionner —</option>
                {candidates.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.public_member_id || c.member_number} — {c.first_name} {c.last_name} · {c.email}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground mt-1">
                Seuls les adhérents actifs, annual, non fondateurs et non complimentary apparaissent ici.
              </p>
            </div>
            <div>
              <Label>Motif</Label>
              <select
                className="w-full h-10 border border-input bg-background px-3 rounded-md text-sm"
                value={form.motif}
                onChange={(e) => setForm(f => ({ ...f, motif: e.target.value }))}
                data-testid="new-payment-motif"
              >
                <option>Adhésion annuelle</option>
                <option>Renouvellement</option>
                <option>Autre</option>
              </select>
            </div>
            <div>
              <Label>Montant (EUR) *</Label>
              <Input
                type="number" step="0.01" min="0.5"
                value={form.amount}
                onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                data-testid="new-payment-amount"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Défaut : {pricing.standard_annual_amount.toFixed(2)} {pricing.currency}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Période — début</Label>
                <Input type="date" value={form.period_start}
                  onChange={(e) => setForm(f => ({ ...f, period_start: e.target.value }))} />
              </div>
              <div>
                <Label>Période — fin</Label>
                <Input type="date" value={form.period_end}
                  onChange={(e) => setForm(f => ({ ...f, period_end: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-3 border-t border-border">
              <Button variant="outline" onClick={() => setNewDialog({ open: false })}>Annuler</Button>
              <Button
                onClick={submitNewPayment}
                disabled={busy || !form.membership_id}
                className="bg-accent hover:bg-accent/90 gap-2"
                data-testid="new-payment-submit"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                Créer le lien SumUp
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
